import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useNegocio } from './AliadoDashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { GitBranch, Pencil, Trash2, Plus, MapPin, Phone } from 'lucide-react';
import { toast } from 'sonner';

const empty = { name: '', address: '', city: '', phone: '', manager: '', active: true };

export default function AliadoBranchesPage() {
    const negocio = useNegocio();
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);
    const [deleteId, setDeleteId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/me/oknegocio/branches');
            setBranches(data);
        } catch { toast.error('Error al cargar sucursales'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setEditing(null);
        setForm({ ...empty, city: negocio?.city || '' });
        setShowForm(true);
    };

    const openEdit = (b) => {
        setEditing(b.id);
        setForm({ name: b.name, address: b.address || '', city: b.city || '', phone: b.phone || '', manager: b.manager || '', active: b.active ?? true });
        setShowForm(true);
    };

    const handleSave = async () => {
        if (!form.name.trim()) return toast.error('El nombre es requerido');
        setSaving(true);
        try {
            if (editing) {
                await api.put(`/me/oknegocio/branches/${editing}`, form);
                toast.success('Sucursal actualizada');
            } else {
                await api.post('/me/oknegocio/branches', form);
                toast.success('Sucursal creada');
            }
            setShowForm(false);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al guardar');
        } finally { setSaving(false); }
    };

    const handleDelete = async () => {
        try {
            await api.delete(`/me/oknegocio/branches/${deleteId}`);
            toast.success('Sucursal eliminada');
            setDeleteId(null);
            load();
        } catch { toast.error('Error al eliminar'); }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-bold flex items-center gap-2"><GitBranch className="h-5 w-5 text-orange-600" /> Sucursales</h1>
                    <p className="text-sm text-muted-foreground">{branches.length} sucursal(es) registrada(s).</p>
                </div>
                <Button size="sm" onClick={openCreate} className="bg-orange-600 hover:bg-orange-700">
                    <Plus className="h-4 w-4 mr-1" /> Agregar sucursal
                </Button>
            </div>

            {loading ? (
                <div className="text-sm text-muted-foreground text-center py-8">Cargando…</div>
            ) : branches.length === 0 ? (
                <div className="rounded-2xl border bg-card p-8 text-center">
                    <GitBranch className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <div className="text-sm text-muted-foreground">Aún no tienes sucursales adicionales. La sede principal es tu negocio registrado.</div>
                </div>
            ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                    {branches.map(b => (
                        <div key={b.id} className="rounded-2xl border bg-card p-4 space-y-2">
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="font-semibold text-sm">{b.name}</div>
                                    {b.manager && <div className="text-xs text-muted-foreground">Encargado: {b.manager}</div>}
                                </div>
                                <div className="flex items-center gap-1">
                                    <Switch checked={b.active} onCheckedChange={async (v) => {
                                        await api.put(`/me/oknegocio/branches/${b.id}`, { active: v });
                                        setBranches(prev => prev.map(x => x.id === b.id ? { ...x, active: v } : x));
                                    }} className="scale-75" />
                                </div>
                            </div>
                            {b.address && (
                                <div className="flex items-start gap-1 text-xs text-muted-foreground">
                                    <MapPin className="h-3 w-3 mt-0.5 shrink-0" />
                                    <span>{b.address}{b.city ? `, ${b.city}` : ''}</span>
                                </div>
                            )}
                            {b.phone && (
                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                    <Phone className="h-3 w-3 shrink-0" />{b.phone}
                                </div>
                            )}
                            <div className="flex gap-1 pt-1">
                                <Button size="sm" variant="ghost" onClick={() => openEdit(b)} className="h-7 px-2 text-xs">
                                    <Pencil className="h-3 w-3 mr-1" /> Editar
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setDeleteId(b.id)} className="h-7 px-2 text-xs text-destructive hover:bg-destructive/10">
                                    <Trash2 className="h-3 w-3" />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={showForm} onOpenChange={setShowForm}>
                <DialogContent className="max-w-sm">
                    <DialogHeader><DialogTitle>{editing ? 'Editar sucursal' : 'Nueva sucursal'}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <label className="text-xs font-medium">Nombre *</label>
                            <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="mt-1" placeholder="Ej. Sucursal Norte" />
                        </div>
                        <div>
                            <label className="text-xs font-medium">Dirección</label>
                            <Input value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} className="mt-1" placeholder="Calle y número" />
                        </div>
                        <div>
                            <label className="text-xs font-medium">Ciudad</label>
                            <Input value={form.city} onChange={e => setForm(p => ({ ...p, city: e.target.value }))} className="mt-1" placeholder="Ciudad de México" />
                        </div>
                        <div>
                            <label className="text-xs font-medium">Teléfono</label>
                            <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="mt-1" placeholder="55 1234 5678" />
                        </div>
                        <div>
                            <label className="text-xs font-medium">Encargado</label>
                            <Input value={form.manager} onChange={e => setForm(p => ({ ...p, manager: e.target.value }))} className="mt-1" placeholder="Nombre del encargado" />
                        </div>
                        <div className="flex items-center gap-2">
                            <Switch id="bactive" checked={form.active} onCheckedChange={v => setForm(p => ({ ...p, active: v }))} />
                            <label htmlFor="bactive" className="text-xs font-medium">Sucursal activa</label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
                        <Button onClick={handleSave} disabled={saving} className="bg-orange-600 hover:bg-orange-700">
                            {saving ? 'Guardando…' : editing ? 'Guardar' : 'Crear'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
                <DialogContent className="max-w-sm">
                    <DialogHeader><DialogTitle>¿Eliminar sucursal?</DialogTitle></DialogHeader>
                    <p className="text-sm text-muted-foreground">Esta acción no se puede deshacer.</p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteId(null)}>Cancelar</Button>
                        <Button variant="destructive" onClick={handleDelete}>Eliminar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
